/* ============================================================
   VIKLUND - Standard-products PDP behaviours
   Loaded from theme.liquid only when template.suffix == 'standard-products'.
   Money stays in viklund-tier-pricing.js; this file owns what must follow
   the variant without a page load:

   1. The tech line under the product name. Its own hook, not vk-pdp.js's
      data-vk-subtitle, because that one writes raw option values and here
      the commas render as middots.
   2. The SKU under it.
   3. The Specifications fact pairs, swapped wholesale from the JSON map
      vk-std-pairs.liquid emits. The dedup rules that build that list run
      in Liquid, once per variant, so nothing is recomputed here and the
      two renderings cannot disagree. Those pairs render in the tabs
      section at the foot of the page now, not in the buy column, which
      is why sectionIdOf falls back to <product-info>.
   4. <vk-std-combo>, the grouped variant picker that replaces the native
      select's unstyleable optgroup headers. Its own block at the foot of
      this file, and the only part here that is a custom element rather
      than a subscription.

   The mobile head lift is NOT here: the reading block carries the class
   vk-pdp-reading, so vk-pdp.js already lifts the title, tech line and SKU
   above the gallery. Two lifts would fight over the same nodes.
   ============================================================ */
(function () {
  'use strict';

  /* Symmetric bind/unbind (same discipline as vk-pdp.js): the theme editor
     re-renders sections in place, so every subscription is dropped before a
     re-init to keep handlers from stacking on surviving DOM. */
  var cleanups = [];
  function teardown() {
    cleanups.forEach(function (fn) { fn(); });
    cleanups = [];
  }

  /* The pairs live in the TABS section now, not in main-product, so their
     own section.id is not the one variantChange carries. They therefore
     ship with no data-section at all and fall through to the page's
     <product-info>, which is the authoritative id in both places. */
  function sectionIdOf(el) {
    if (el.dataset.section) return el.dataset.section;
    var pi = el.closest('product-info') || document.querySelector('product-info[data-section]');
    return pi ? pi.dataset.section : '';
  }

  /* One subscription per consumer, each guarded on its own section id.
     An unavailable option combination arrives with variant null: keep the
     last valid state rather than blanking the column. */
  function onVariantChange(sectionId, fn) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (!PUB_SUB_EVENTS.variantChange) return;
    cleanups.push(subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data || event.data.sectionId !== sectionId) return;
      var v = event.data.variant;
      if (v && v.id != null) fn(v);
    }));
  }

  function readJson(root, selector) {
    try {
      var el = root.querySelector(selector);
      return el ? JSON.parse(el.textContent) || {} : {};
    } catch (e) {
      return {};
    }
  }

  /* ---- 1. Tech line under the product name ---- */
  function initSubtitle() {
    var el = document.querySelector('[data-vk-std-subtitle]');
    if (!el) return;
    onVariantChange(sectionIdOf(el), function (variant) {
      var parts = [];
      [variant.option1, variant.option2, variant.option3].forEach(function (opt) {
        if (!opt) return;
        // "102 x 152 mm, 300 labels, 25 mm core" reads as three facts, so
        // it is set as three, the same separator the rest of the PDP uses.
        opt.split(',').forEach(function (piece) {
          var t = piece.trim();
          if (t) parts.push(t);
        });
      });
      if (parts.length) el.textContent = parts.join(' · ');
    });
  }

  /* ---- 2. SKU ---- */
  function initSku() {
    var el = document.querySelector('[data-vk-std-sku]');
    if (!el) return;
    onVariantChange(sectionIdOf(el), function (variant) {
      var sku = variant.sku == null ? '' : String(variant.sku).trim();
      el.textContent = sku;
      el.hidden = sku === '';
    });
  }

  /* ---- 3. Product information fact pairs ---- */
  function initPairs() {
    var root = document.querySelector('[data-vk-std-pairs]');
    if (!root) return;
    var list = root.querySelector('[data-vk-std-pairs-list]');
    if (!list) return;
    var map = readJson(root, '[data-vk-pairs-map]');

    onVariantChange(sectionIdOf(root), function (variant) {
      var rows = map[String(variant.id)];
      if (!rows) return; // unknown variant: keep the pairs that are up
      while (list.firstChild) list.removeChild(list.firstChild);
      rows.forEach(function (r) {
        var wrap = document.createElement('div');
        wrap.className = 'vk-std-pair';
        var dt = document.createElement('dt');
        dt.textContent = r.label; // textContent: metafield values stay inert
        var dd = document.createElement('dd');
        dd.textContent = r.value;
        wrap.appendChild(dt);
        wrap.appendChild(dd);
        list.appendChild(wrap);
      });
      // A variant with no facts at all would leave a tab that opens onto
      // nothing; hide the whole block instead.
      root.hidden = rows.length === 0;
    });
  }

  function init() {
    teardown();
    initSubtitle();
    initSku();
    initPairs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor lifecycle (the storefront never fires these): debounce to
  // one re-init after the DOM settles, same as vk-pdp.js.
  var reinitQueued = false;
  function queueReinit() {
    if (reinitQueued) return;
    reinitQueued = true;
    setTimeout(function () {
      reinitQueued = false;
      init();
    }, 0);
  }
  document.addEventListener('shopify:section:load', queueReinit);
  document.addEventListener('shopify:section:unload', queueReinit);
})();

/* ============================================================
   VIKLUND - <vk-std-combo>: the grouped variant picker

   A native <select> draws its <optgroup> labels with the browser's own
   style, grey and italic, and no CSS reaches them. On this control the
   group labels are the sizes, so they came out LIGHTER than the rows
   underneath and stopped reading as headers at all. This element builds
   a button and a listbox out of the select's own optgroups and options,
   then hides the select.

   It is an upgrade, not a replacement. The markup ships as a working
   grouped <select>; this file only takes over once it runs, and the
   select stays in the form as the value source. Selecting a row writes
   the value back to it and fires change, which is the only thing Dawn's
   VariantSelects listens for.

   No state is kept between variants: Dawn's product-info refetches the
   section and swaps the whole <variant-selects> node on every change,
   so each new node builds itself from a select that already carries the
   right selected option. The one thing that does not survive the swap is
   focus, hence the module flag below.
   ============================================================ */
(function () {
  'use strict';

  if (typeof window.customElements === 'undefined') return;
  if (window.customElements.get('vk-std-combo')) return;

  // Set at the moment a keyboard commit fires change, read by the next
  // element's connectedCallback. A mouse commit does not set it: putting a
  // focus ring back under the pointer is noise.
  var restoreFocus = false;

  var KEY_NEXT = ['ArrowDown', 'Down'];
  var KEY_PREV = ['ArrowUp', 'Up'];

  function isKey(event, list) {
    return list.indexOf(event.key) !== -1;
  }

  class VkStdCombo extends HTMLElement {
    connectedCallback() {
      if (this.vkBuilt) return;
      this.select = this.querySelector('select');
      this.field = this.querySelector('.select');
      if (!this.select || !this.field) return;
      this.build();
      this.vkBuilt = true;
      if (restoreFocus) {
        restoreFocus = false;
        this.button.focus();
      }
    }

    disconnectedCallback() {
      this.close(false);
    }

    build() {
      var self = this;
      var uid = String(this.select.id || 'vk-combo').replace(/[^\w-]/g, '');
      var label = this.parentNode ? this.parentNode.querySelector('.form__label') : null;
      if (label && !label.id) label.id = 'VkComboLabel-' + uid;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'select__select vk-std-combo__button';
      button.id = 'VkComboButton-' + uid;
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', 'VkComboList-' + uid);
      if (label) button.setAttribute('aria-labelledby', label.id + ' ' + button.id);

      var value = document.createElement('span');
      value.className = 'vk-std-combo__value';
      button.appendChild(value);

      var panel = document.createElement('div');
      panel.className = 'vk-std-combo__panel';
      panel.hidden = true;

      var list = document.createElement('div');
      list.className = 'vk-std-combo__list';
      list.id = 'VkComboList-' + uid;
      list.setAttribute('role', 'listbox');
      list.tabIndex = -1;
      if (label) list.setAttribute('aria-labelledby', label.id);
      panel.appendChild(list);

      this.rows = [];
      var addRow = function (option, into) {
        var row = document.createElement('div');
        row.className = 'vk-std-combo__opt';
        row.id = 'VkComboOpt-' + uid + '-' + self.rows.length;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        // textContent, not innerHTML: the row text comes from variant names.
        row.textContent = String(option.textContent).trim();
        row.setAttribute('data-vk-value', option.value);
        into.appendChild(row);
        self.rows.push(row);
      };

      Array.prototype.forEach.call(this.select.children, function (child, i) {
        if (child.tagName === 'OPTGROUP') {
          var group = document.createElement('div');
          group.className = 'vk-std-combo__group';
          group.setAttribute('role', 'group');
          var head = document.createElement('div');
          head.className = 'vk-std-combo__grouphead';
          head.id = 'VkComboGroup-' + uid + '-' + i;
          head.textContent = child.label;
          group.setAttribute('aria-labelledby', head.id);
          group.appendChild(head);
          Array.prototype.forEach.call(child.children, function (option) {
            if (option.tagName === 'OPTION') addRow(option, group);
          });
          list.appendChild(group);
        } else if (child.tagName === 'OPTION') {
          addRow(child, list);
        }
      });

      this.button = button;
      this.panel = panel;
      this.list = list;
      this.value = value;
      this.activeIndex = this.selectedIndex();
      value.textContent = this.rows[this.activeIndex] ? this.rows[this.activeIndex].textContent : '';

      this.field.appendChild(button);
      this.field.appendChild(panel);
      this.setAttribute('data-vk-ready', '');

      // The label points at a select nobody can see any more. A button is
      // not a labelable element, so the name moves to aria-labelledby and
      // the click is wired by hand rather than left dead.
      if (label) {
        label.removeAttribute('for');
        label.addEventListener('click', function () {
          button.focus();
        });
      }

      button.addEventListener('click', function () {
        self.isOpen ? self.close(true) : self.open();
      });
      button.addEventListener('keydown', function (event) {
        if (isKey(event, KEY_NEXT) || isKey(event, KEY_PREV)) {
          event.preventDefault();
          self.open();
        }
      });

      list.addEventListener('keydown', function (event) {
        self.onListKeydown(event);
      });
      list.addEventListener('click', function (event) {
        var row = event.target.closest('.vk-std-combo__opt');
        if (!row) return;
        self.commit(self.rows.indexOf(row), false);
      });
      list.addEventListener('mousemove', function (event) {
        var row = event.target.closest('.vk-std-combo__opt');
        if (!row) return;
        self.setActive(self.rows.indexOf(row), false);
      });

      this.onDocumentDown = function (event) {
        if (!self.contains(event.target)) self.close(false);
      };
    }

    selectedIndex() {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].getAttribute('aria-selected') === 'true') return i;
      }
      return 0;
    }

    open() {
      if (this.isOpen || !this.rows.length) return;
      this.isOpen = true;
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-vk-open', '');
      this.list.focus();
      this.setActive(this.selectedIndex(), true);
      document.addEventListener('mousedown', this.onDocumentDown);
    }

    close(focusButton) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-vk-open');
      this.list.removeAttribute('aria-activedescendant');
      document.removeEventListener('mousedown', this.onDocumentDown);
      if (focusButton) this.button.focus();
    }

    setActive(index, scroll) {
      var row = this.rows[index];
      if (!row) return;
      var previous = this.rows[this.activeIndex];
      if (previous) previous.classList.remove('vk-std-combo__opt--active');
      this.activeIndex = index;
      row.classList.add('vk-std-combo__opt--active');
      this.list.setAttribute('aria-activedescendant', row.id);
      if (scroll && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    }

    onListKeydown(event) {
      if (isKey(event, KEY_NEXT)) {
        event.preventDefault();
        this.setActive(Math.min(this.activeIndex + 1, this.rows.length - 1), true);
      } else if (isKey(event, KEY_PREV)) {
        event.preventDefault();
        this.setActive(Math.max(this.activeIndex - 1, 0), true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.setActive(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.setActive(this.rows.length - 1, true);
      } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.commit(this.activeIndex, true);
      } else if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'Tab') {
        // No preventDefault: focus moves to the button synchronously, so the
        // browser's own Tab then carries on from there.
        this.close(true);
      }
    }

    commit(index, viaKeyboard) {
      var row = this.rows[index];
      if (!row) return;
      var value = row.getAttribute('data-vk-value');
      this.close(true);
      if (this.select.value === value) return;

      // Paint the choice now. The section refetch that follows will replace
      // this whole element, but that is a network round trip away.
      this.rows.forEach(function (other) {
        other.setAttribute('aria-selected', other === row ? 'true' : 'false');
      });
      this.value.textContent = row.textContent;

      if (viaKeyboard) restoreFocus = true;
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  window.customElements.define('vk-std-combo', VkStdCombo);
})();
